import type { Result } from '../core/result';
import { ok, err } from '../core/result';
import type { ResidualWarp } from '../data/generated/painterView';
import { createAjvFactory } from '../data/validate';
import painterViewSchema from '../data/schema/painterView.schema.json';
import { residualWarp as committed } from '../data/painterView.json';
import type { PainterResidualWarp, ThinPlateField, VerticalShiftField } from './painterWarp';

/*
 * tools/painterWarp fits the warp offline and the build bundles its section of painterView.json,
 * so the app neither decodes the painting nor fits anything at load; the section is checked against
 * the same schema as the file the app fetches.
 */
const sectionSchema = {
  $schema: painterViewSchema.$schema,
  $defs: painterViewSchema.$defs,
  $ref: '#/$defs/ResidualWarp',
};

let validate: ReturnType<ReturnType<typeof createAjvFactory>['compile']> | null = null;

interface SplineSection {
  readonly centresPx: readonly (readonly number[])[];
  readonly weights: readonly number[];
  readonly affine: readonly number[];
}

function unpaired(name: string, spline: SplineSection): string[] {
  return spline.centresPx.length === spline.weights.length
    ? []
    : [`${name} has ${spline.centresPx.length} centres and ${spline.weights.length} weights`];
}

/* The column shift keeps the spline unit of the row shift, as the section's schema states. */
function fieldOf(spline: SplineSection, scalePx: number): ThinPlateField {
  return {
    scalePx,
    centresPx: spline.centresPx.map((c) => [c[0]!, c[1]!] as const),
    weights: [...spline.weights],
    affine: [spline.affine[0]!, spline.affine[1]!, spline.affine[2]!],
  };
}

function verticalOf(section: ResidualWarp): { vertical?: VerticalShiftField } {
  const column = section.vertical;
  if (!column) return {};
  return {
    vertical: {
      heldRowPx: column.heldRowPx,
      lastFittedRowPx: column.lastFittedRowPx,
      ...fieldOf(column, section.scalePx),
    },
  };
}

/* The column shift holds below its lowest centre, the lowest point it was fitted on. */
function unheld(name: string, column: ResidualWarp['vertical']): string[] {
  if (!column) return [];
  const lowest = Math.max(...column.centresPx.map((c) => c[1]));
  return column.lastFittedRowPx === lowest
    ? []
    : [
        `${name} holds below row ${String(column.lastFittedRowPx)}, not its lowest centre's ${String(lowest)}`,
      ];
}

export function parseResidualWarp(value: unknown): Result<PainterResidualWarp, readonly string[]> {
  validate ??= createAjvFactory().compile(sectionSchema);
  if (!validate(value)) {
    return err(
      (validate.errors ?? []).map((e) => `${e.instancePath || '/'}: ${e.message ?? 'invalid'}`)
    );
  }
  const section = value as ResidualWarp;
  const problems = [
    ...unpaired('residualWarp', section),
    ...(section.vertical ? unpaired('residualWarp vertical', section.vertical) : []),
    ...unheld('residualWarp vertical', section.vertical),
  ];
  if (problems.length > 0) return err(problems);
  return ok({ ...fieldOf(section, section.scalePx), ...verticalOf(section) });
}

export function committedResidualWarp(): PainterResidualWarp {
  const parsed = parseResidualWarp(committed);
  if (!parsed.ok) {
    throw new Error(`painterView.json residualWarp is invalid: ${parsed.error.join('; ')}`);
  }
  return parsed.value;
}
